import React, { useState } from 'react'
import { capitalize } from '@sketch/utils'
import { useHandleCopyValue } from '@sketch/modules-common'
import {
  Flex,
  Box,
  CopyToClipboard,
  Tooltip,
  Separator,
} from '@sketch/components'
import { PopoverHeader } from '../../../../../PopoverHeader'
import ColorDropdown from '../../../../../../../Inspector/Sidebar/components/ColorDropdown'
import ConnectedColorDot from './ConnectedColorDot'
import {
  Stops,
  StopContainer,
  StopPercentage,
  PopoverButtonContainer,
  ColorSwatch,
  SwatchContainer,
  SwatchBackground,
  CopyIcon,
  CopyButton,
  ColorDropdownIcon,
  ColorDropdownToggle,
  ColorSpaceValue,
} from './GradientPopover.styles'
import rgbTo, {
  ColorFormat,
} from '../../../../../../../Inspector/Sidebar/components/Color/utils'
import { Gradient } from '../../../../../../../../inspector'
import { GradientColorInterpolation } from '../../../../../../../../inspector/types/InspectorTypes/Enums'
import { ColorValue } from './ColorValue'
import {
  gradientBackground,
  gradientAngle,
} from '../../../../../../../Inspector/Sidebar/components/GradientAttribute/math'
import { AttributeList } from '../../../Attribute'
import { FullCopyAttribute } from '../../../FullCopyAttribute'

const getColorSpaceName = (
  colorInterpolation: GradientColorInterpolation
): string | null => {
  switch (colorInterpolation) {
    case GradientColorInterpolation.Rgb:
      // Don't display rgb color space since it's the default
      return null
    case GradientColorInterpolation.Oklab:
      return 'Perceptual (Oklab)'
    case GradientColorInterpolation.Oklch:
      return 'Vibrant (Oklch)'
    default:
      return colorInterpolation
  }
}

interface StopProps {
  stopPosition: number
  red: number
  green: number
  blue: number
  alpha?: number
  colorFormat: ColorFormat
  colorVariableName?: string
  onColorFormatChange: (f: ColorFormat) => void
  connected: boolean
}

const Stop: React.FCC<StopProps> = ({
  stopPosition,
  red,
  green,
  blue,
  alpha = 1,
  colorFormat,
  onColorFormatChange,
  connected,
  colorVariableName,
}) => {
  const [dropdownVisible, setDropdownVisible] = useState(false)
  const copyValue = rgbTo(colorFormat, { red, green, blue, alpha })

  return (
    <CopyToClipboard
      value={copyValue}
      placement="left"
      disabled={dropdownVisible}
      showIcon={false}
      opacityFull
      dropdown={
        <PopoverButtonContainer>
          <ColorDropdown
            customToggle={
              <ColorDropdownToggle data-testid="dropdown-button">
                <ColorDropdownIcon />
              </ColorDropdownToggle>
            }
            onChange={newFormat => {
              onColorFormatChange(newFormat)
            }}
            dropdownOpen={setDropdownVisible}
          />
        </PopoverButtonContainer>
      }
    >
      <StopContainer connected={connected}>
        <StopPercentage>{`${Math.round(stopPosition * 100)}%`}</StopPercentage>
        <ConnectedColorDot
          red={red}
          green={green}
          blue={blue}
          alpha={alpha}
          connected={connected}
        />
        <ColorValue
          red={red}
          green={green}
          blue={blue}
          alpha={alpha}
          format={colorFormat}
          colorVariableName={colorVariableName}
        />
      </StopContainer>
    </CopyToClipboard>
  )
}

const gradientColors: (
  gradient: Gradient,
  colorModifiers: {
    onColorFormatChange: (f: ColorFormat) => void
    colorFormat: ColorFormat
  }
) => React.ReactNode[] = (gradient, colorModifiers) => {
  const stopsLength = gradient.stops.length
  const stops = [...gradient.stops]
    .sort((lhs, rhs) => lhs.position - rhs.position)
    // Remove duplicated stops (same position, same color)
    .filter(
      (stop, index, self) =>
        index === self.findIndex(t => t.position === stop.position)
    )
    .map((stop, i) => {
      const connected = stopsLength !== i + 1

      return (
        <Stop
          stopPosition={stop.position}
          red={stop.color.red}
          green={stop.color.green}
          blue={stop.color.blue}
          alpha={stop.color.alpha}
          connected={connected}
          colorVariableName={stop.color.colorVariableName}
          key={stop.position}
          {...colorModifiers}
        />
      )
    })
  return stops
}

interface GradientContentProps {
  gradient: Gradient
  onColorFormatChange: (f: ColorFormat) => void
  colorFormat: ColorFormat
  copyValue: string
  onClose?: () => void
}

export const GradientContent = ({
  gradient,
  onColorFormatChange,
  colorFormat,
  copyValue,
  onClose,
}: GradientContentProps) => {
  const background = gradientBackground(gradient)
  const radius = gradientAngle(gradient)

  const {
    handleCopyValue,
    copyTooltipText,
    handleEnterCopiableArea,
    handleLeaveCopiableArea,
    copyTooltipVisible,
  } = useHandleCopyValue(copyValue)

  const colorSpace = getColorSpaceName(gradient.colorInterpolation)

  return (
    <>
      <PopoverHeader
        onClose={onClose}
        title="Gradient"
        extraAction={
          <Tooltip content={copyTooltipText} visible={copyTooltipVisible}>
            <Flex alignItems="center">
              <CopyButton
                onClick={handleCopyValue}
                onMouseEnter={handleEnterCopiableArea}
                onMouseLeave={handleLeaveCopiableArea}
              >
                <CopyIcon />
              </CopyButton>
            </Flex>
          </Tooltip>
        }
      />
      <SwatchContainer>
        <SwatchBackground />
        <ColorSwatch background={background} />
      </SwatchContainer>
      <Box pl={5}>
        <AttributeList>
          <FullCopyAttribute label="Direction" value={`${radius}º`} />
          <FullCopyAttribute label="Type" value={capitalize(gradient.type)} />
          {colorSpace && (
            <ColorSpaceValue>
              <span>{colorSpace}</span>
            </ColorSpaceValue>
          )}
        </AttributeList>
      </Box>
      <Separator mt={2} />
      <Stops>
        {gradientColors(gradient, { onColorFormatChange, colorFormat })}
      </Stops>
    </>
  )
}
